import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {
    createImageOpImgProc,
    isImageOpImgProc,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"
import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/math/definition"
import {ColorLike, isColorLike} from "@cm/math"
import {ImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc"
import {assertNever} from "@cm/utils"
import {ImageOpPerPixelFn} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/node"
import {HalCompositorInfo} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/image-op-per-pixel-fn/hal"
import {ImageOpInputsOrNodes, ImageOpParameters} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {Descriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"

const nodeDefinition = {...definition, nodeClass: "ImageOpNodeMath"}

@registerImageOpNode
export class ImageOpNodeMath extends ImageOpPerPixelFn<typeof definition> {
    override get definition() {
        return nodeDefinition
    }

    constructor(parameters: ImageOpInputsOrNodes<typeof definition>) {
        super(parameters)
    }

    protected getCompositingInfo(): HalCompositorInfo {
        return {
            compositingFunction: this.getCompositingFunction(),
            sourceParameterNames: this.getSourceParameterNames(),
            outDescriptor: this.getOutDescriptor(),
        }
    }

    private getCompositingFunction() {
        switch (this.parameters.operator) {
            case "length":
            case "sqrt":
            case "square":
            case "cos":
            case "sin":
            case "clip01":
            case "abs":
            case "isNaN":
                return `
                    vec4 operand = fetchSource(0);
                    ${ImageOpNodeMath.getOperatorFunction(this.parameters)}
                `
            case "+":
            case "-":
            case "*":
            case "/":
            case "/safe":
            case ">":
            case "<":
            case ">=":
            case "<=":
            case "==":
            case "max":
            case "min":
            case "atan2":
            case "pow":
            case "mod":
            case "distance":
                return `
                    vec4 operandA = fetchSource(0);
                    vec4 operandB = fetchSource(1);
                    ${ImageOpNodeMath.getOperatorFunction(this.parameters)}
                `
            default:
                assertNever(this.parameters)
        }
    }

    static getOperatorFunction(parameters: ImageOpParameters<typeof definition>) {
        switch (parameters.operator) {
            case "length":
                return "return length(operand);"
            case "sqrt":
                return "return sqrt(operand);"
            case "square":
                return "return operand * operand;"
            case "cos":
                return "return cos(operand);"
            case "sin":
                return "return sin(operand);"
            case "clip01":
                return "return clamp(operand, 0.0, 1.0);"
            case "abs":
                return "return abs(operand);"
            case "isNaN":
                return "return isnan(operand);"
            case "+":
                return "return operandA + operandB;"
            case "-":
                return "return operandA - operandB;"
            case "*":
                return "return operandA * operandB;"
            case "/":
                return "return operandA / operandB;"
            case "/safe":
                return "return vec4((operandB.r == 0.0) ? 0.0 : (operandA.r / operandB.r), (operandB.g == 0.0) ? 0.0 : (operandA.g / operandB.g), (operandB.b == 0.0) ? 0.0 : (operandA.b / operandB.b), (operandB.a == 0.0) ? 0.0 : (operandA.a / operandB.a));"
            case ">":
                return "return operandA > operandB ? 1.0 : 0.0;"
            case "<":
                return "return operandA < operandB ? 1.0 : 0.0;"
            case ">=":
                return "return operandA >= operandB ? 1.0 : 0.0;"
            case "<=":
                return "return operandA <= operandB ? 1.0 : 0.0;"
            case "==":
                return "return operandA == operandB ? 1.0 : 0.0;"
            case "max":
                return "return max(operandA, operandB);"
            case "min":
                return "return min(operandA, operandB);"
            case "atan2":
                return "return atan2(operandA, operandB);"
            case "pow":
                return "return pow(operandA, operandB);"
            case "mod":
                return "return mod(operandA, operandB);"
            case "distance":
                return "return vec4(distance(operandA, operandB));"
            default:
                assertNever(parameters)
        }
    }

    private getSourceParameterNames() {
        switch (this.parameters.operator) {
            case "length":
            case "sqrt":
            case "square":
            case "cos":
            case "sin":
            case "clip01":
            case "abs":
            case "isNaN":
                return ["operand"]
            case "+":
            case "-":
            case "*":
            case "/":
            case "/safe":
            case ">":
            case "<":
            case ">=":
            case "<=":
            case "==":
            case "max":
            case "min":
            case "atan2":
            case "pow":
            case "mod":
            case "distance":
                return ["operandA", "operandB"]
            default:
                assertNever(this.parameters)
        }
    }

    private getOutDescriptor(): Partial<Descriptor> | undefined {
        switch (this.parameters.operator) {
            case "length":
            case "distance":
                return {
                    channels: 1,
                }
            default:
                return undefined
        }
    }

    override toImgProc = async (evaluator: Evaluator<ImageOpContextImgProc>, parameters: ImageOpParametersOfType<typeof definition, "imgproc">) => {
        const evaluatedParameters = await evaluator.getAll(parameters)
        const operator = evaluatedParameters.operator
        if (operator === "length" || operator === "distance") {
            throw new Error("Operation not supported in image-processing: " + operator)
        }
        const operandA = "operandA" in evaluatedParameters ? evaluatedParameters.operandA : evaluatedParameters.operand
        const operandB = "operandB" in evaluatedParameters ? evaluatedParameters.operandB : undefined
        const numPatchesA = (isImageOpImgProc(operandA) ? operandA.descriptor.batching?.numPatches : undefined) ?? {x: 1, y: 1}
        const numPatchesB = (isImageOpImgProc(operandB) ? operandB.descriptor.batching?.numPatches : undefined) ?? {x: 1, y: 1}
        if (numPatchesA.x !== 1 || numPatchesA.y !== 1 || numPatchesB.x !== 1 || numPatchesB.y !== 1) {
            throw new Error("Batch size not supported in image-processing")
        }
        if (Array.isArray(operandB)) {
            throw new Error("Array operands currently not supported in image-processing")
        }
        // a color operand is broadcast as a 1x1 constant image matching the other operand's
        // channel count, which mathOperation already treats as a per-channel constant
        const toColorConstant = (color: ColorLike, reference: ImageOpImgProc) =>
            evaluator.context.createEmptyImage({width: 1, height: 1, channels: reference.descriptor.channels}, color)

        let imageOperandA: ImageOpImgProc
        if (typeof operandA === "number") {
            // image-processing does not allow the first operand to be a number; let's make it an image first
            if (!isImageOpImgProc(operandB)) {
                throw new Error("Invalid operand")
            }
            imageOperandA = createImageOpImgProc(operandB.descriptor, {
                type: "math",
                operation: "constLike",
                firstInput: operandB.node,
                secondInput: operandA,
            })
        } else if (isColorLike(operandA)) {
            if (!isImageOpImgProc(operandB)) {
                throw new Error("A color operand requires the other operand to be an image")
            }
            imageOperandA = toColorConstant(operandA, operandB)
        } else if (Array.isArray(operandA)) {
            throw new Error("Array operands currently not supported in image-processing")
        } else {
            imageOperandA = operandA
        }
        const resolvedOperandB = isColorLike(operandB) ? toColorConstant(operandB, imageOperandA) : operandB
        const descriptor = this.determineDescriptor(evaluatedParameters)
        return createImageOpImgProc(descriptor, {
            type: "math",
            operation: operator,
            firstInput: imageOperandA.node,
            secondInput: resolvedOperandB instanceof ImageOpImgProc ? resolvedOperandB.node : resolvedOperandB,
        })
    }
}
